import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import {
    getKitchenOrders,
} from "../../../api/foodApi";

import type {
    FoodOrder,
    FoodOrderStatus,
} from "./foodTypes";

import {
    getOrderBoxCount,
} from "./foodEstimate";

import "./playerLoungeFood.css";

function normalizeStatus(status: any): FoodOrderStatus {
    if (
        status === "Received" ||
        status === "InProgress" ||
        status === "Ready" ||
        status === "Delivered" ||
        status === "Cancelled"
    ) {
        return status;
    }

    switch (Number(status)) {
        case 0:
            return "Received";
        case 1:
            return "InProgress";
        case 2:
            return "Ready";
        case 3:
            return "Delivered";
        case 4:
            return "Cancelled";
        default:
            return "Received";
    }
}

export default function FoodOrderHistoryPage() {
    const { eventId } =
        useParams();

    const parsedEventId =
        Number(eventId);

    const [orders, setOrders] =
        useState<FoodOrder[]>([]);

    const [loading, setLoading] =
        useState(true);

    const [error, setError] =
        useState("");

    const [search, setSearch] =
        useState("");

    const [statusFilter, setStatusFilter] =
        useState<
            "All" |
            "Delivered" |
            "Cancelled"
        >("All");

    const load =
        useCallback(async () => {
            if (!parsedEventId)
                return;

            setLoading(true);

            try {
                const data =
                    await getKitchenOrders(
                        parsedEventId
                    );

                setOrders(
                    data.map(order => ({
                        ...order,

                        status:
                            normalizeStatus(
                                order.status
                            ),
                    }))
                );

                setError("");
            } catch (err) {
                setError(
                    err instanceof Error
                        ? err.message
                        : "Kunne ikke hente historik."
                );
            } finally {
                setLoading(false);
            }
        }, [parsedEventId]);

    useEffect(() => {
        load();
    }, [load]);

    const historyOrders =
        useMemo(() => {
            const q =
                search
                    .trim()
                    .toLowerCase();

            return orders
                .filter(order => {
                    const status =
                        normalizeStatus(
                            order.status
                        );

                    if (
                        status !== "Delivered" &&
                        status !== "Cancelled"
                    ) {
                        return false;
                    }

                    if (
                        statusFilter !== "All" &&
                        status !== statusFilter
                    ) {
                        return false;
                    }

                    if (!q)
                        return true;

                    const searchText = [
                        order.id,
                        order.personId,
                        order.personName,
                        order.note,
                        order.scheduledFor,
                        ...order.lines.map(
                            line =>
                                line.name
                        ),
                    ]
                        .filter(Boolean)
                        .join(" ")
                        .toLowerCase();

                    return searchText.includes(q);
                })
                .sort(
                    (a, b) =>
                        new Date(
                            b.updatedAt
                        ).getTime() -
                        new Date(
                            a.updatedAt
                        ).getTime()
                );
        }, [
            orders,
            search,
            statusFilter,
        ]);

    return (
        <>
            <header className="pl-topbar">
                <div>
                    <h1>
                        Historik
                    </h1>

                    <div className="pl-steps">
                        Tidligere Player
                        Lounge-ordrer
                    </div>
                </div>

                <button
                    className="pl-history-refresh"
                    onClick={load}
                    disabled={loading}
                >
                    ↻ Opdater
                </button>
            </header>

            <div className="pl-content">
                <div className="pl-orders-toolbar">
                    <div className="pl-order-filters">
                        <button
                            className={
                                statusFilter ===
                                    "All"
                                    ? "active"
                                    : ""
                            }
                            onClick={() =>
                                setStatusFilter(
                                    "All"
                                )
                            }
                        >
                            Alle
                        </button>

                        <button
                            className={
                                statusFilter ===
                                    "Delivered"
                                    ? "active"
                                    : ""
                            }
                            onClick={() =>
                                setStatusFilter(
                                    "Delivered"
                                )
                            }
                        >
                            Udleveret
                        </button>

                        <button
                            className={
                                statusFilter ===
                                    "Cancelled"
                                    ? "active"
                                    : ""
                            }
                            onClick={() =>
                                setStatusFilter(
                                    "Cancelled"
                                )
                            }
                        >
                            Annulleret
                        </button>
                    </div>

                    <input
                        className="pl-search"
                        placeholder="Søg historik..."
                        value={search}
                        onChange={e =>
                            setSearch(
                                e.target.value
                            )
                        }
                    />
                </div>

                {error && (
                    <div className="pl-message">
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="pl-page-loading">
                        Henter historik...
                    </div>
                ) : historyOrders.length === 0 ? (
                    <div className="pl-empty-state">
                        <strong>
                            Ingen historik endnu
                        </strong>

                        <span>
                            Udleverede og
                            annullerede ordrer
                            vises her.
                        </span>
                    </div>
                ) : (
                    <div className="pl-history-table-wrapper">
                        <table className="pl-history-table">
                            <thead>
                                <tr>
                                    <th>
                                        Ordre
                                    </th>

                                    <th>
                                        Person
                                    </th>

                                    <th>
                                        Varer
                                    </th>

                                    <th>
                                        Oprettet
                                    </th>

                                    <th>
                                        Forudbestilt til
                                    </th>

                                    <th>
                                        Bokse
                                    </th>

                                    <th>
                                        Afsluttet
                                    </th>

                                    <th>
                                        Status
                                    </th>

                                    <th>
                                        Total
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {historyOrders.map(
                                    order => {
                                        const status =
                                            normalizeStatus(
                                                order.status
                                            );

                                        return (
                                            <tr
                                                key={
                                                    order.id
                                                }
                                            >
                                                <td>
                                                    <strong>
                                                        #
                                                        {
                                                            order.id
                                                        }
                                                    </strong>
                                                </td>

                                                <td>
                                                    <strong>
                                                        {order.personName ||
                                                            `Person #${order.personId}`}
                                                    </strong>

                                                    <small>
                                                        ID{" "}
                                                        {
                                                            order.personId
                                                        }
                                                    </small>
                                                </td>

                                                <td>
                                                    <div className="pl-history-items">
                                                        {order.lines.map(
                                                            (
                                                                line,
                                                                index
                                                            ) => (
                                                                <span
                                                                    key={`${line.menuItemId}-${index}`}
                                                                >
                                                                    {line.quantity >
                                                                        1
                                                                        ? `${line.quantity} × `
                                                                        : ""}

                                                                    {
                                                                        line.name
                                                                    }
                                                                </span>
                                                            )
                                                        )}

                                                        {order.note && (
                                                            <small
                                                                style={{
                                                                    display: "block",
                                                                    marginTop: "6px",
                                                                }}
                                                            >
                                                                Bemærkning: {order.note}
                                                            </small>
                                                        )}
                                                    </div>
                                                </td>

                                                <td>
                                                    {new Date(
                                                        order.createdAt
                                                    ).toLocaleString(
                                                        "da-DK",
                                                        {
                                                            day: "2-digit",
                                                            month: "2-digit",
                                                            hour: "2-digit",
                                                            minute: "2-digit",
                                                        }
                                                    )}
                                                </td>

                                                <td>
                                                    {order.scheduledFor
                                                        ? new Date(
                                                            order.scheduledFor
                                                        ).toLocaleString(
                                                            "da-DK",
                                                            {
                                                                day: "2-digit",
                                                                month: "2-digit",
                                                                hour: "2-digit",
                                                                minute: "2-digit",
                                                            }
                                                        )
                                                        : "—"}
                                                </td>

                                                <td>
                                                    <strong>
                                                        {getOrderBoxCount(order)}
                                                    </strong>
                                                </td>

                                                <td>
                                                    {new Date(
                                                        order.updatedAt
                                                    ).toLocaleString(
                                                        "da-DK",
                                                        {
                                                            day: "2-digit",
                                                            month: "2-digit",
                                                            hour: "2-digit",
                                                            minute: "2-digit",
                                                        }
                                                    )}
                                                </td>

                                                <td>
                                                    <span
                                                        className={`pl-order-status status-${status}`}
                                                    >
                                                        {status ===
                                                            "Delivered"
                                                            ? "Udleveret"
                                                            : "Annulleret"}
                                                    </span>
                                                </td>

                                                <td>
                                                    <strong>
                                                        {order.total.toFixed(
                                                            0
                                                        )}{" "}
                                                        kr.
                                                    </strong>
                                                </td>
                                            </tr>
                                        );
                                    }
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </>
    );
}